import { useNavigate } from '@tanstack/react-router';
import { forwardRef, useRef, useState } from 'react';

import { Avatar, Button, Input } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAcceptInvitation, useCreateGroup } from '@/lib/queries';
import { NX } from '@/lib/tokens';

import { AuthShell } from './AuthShell';

type Choice = 'create' | 'join' | null;

export function OnboardingScreen() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const createGroup = useCreateGroup();
  const acceptInvitation = useAcceptInvitation();

  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [choice, setChoice] = useState<Choice>(null);
  const [groupName, setGroupName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Radiogroup "Créer" / "Rejoindre" (MAN-119) : roving tabindex à 2 options,
  // donc "l'autre" option est toujours la cible d'une flèche quelle que soit
  // sa direction (Haut/Gauche ou Bas/Droite) — pas besoin de calculer un
  // index dans un tableau pour 2 éléments.
  const createOptionRef = useRef<HTMLDivElement>(null);
  const joinOptionRef = useRef<HTMLDivElement>(null);
  const optionRef = { create: createOptionRef, join: joinOptionRef } as const;

  const selectChoice = (next: Exclude<Choice, null>) => {
    setChoice(next);
    optionRef[next].current?.focus();
  };

  const otherChoice = (current: Exclude<Choice, null>): Exclude<Choice, null> =>
    current === 'create' ? 'join' : 'create';

  const handleOptionKeyDown = (own: Exclude<Choice, null>) => (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        selectChoice(otherChoice(own));
        break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        selectChoice(own);
        break;
    }
  };

  const userName = user?.displayName ?? 'toi';

  const submitChoice = async () => {
    setError('');
    if (choice === 'create' && !groupName.trim()) return;
    if (choice === 'join' && !joinCode.trim()) return;
    if (!choice) return;
    setBusy(true);
    try {
      if (choice === 'create') {
        await createGroup.mutateAsync({ name: groupName.trim() });
      } else {
        // Accepte le slug brut OU une URL d'invitation type https://nexusapp.chat/invite/<slug>
        const slug = joinCode.trim().split('/').filter(Boolean).pop() ?? joinCode.trim();
        await acceptInvitation.mutateAsync(slug);
      }
      setStep(2);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue. Réessaie.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      {/* Progression */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 28 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: i === step ? 24 : 8,
              height: 8,
              borderRadius: 4,
              background: i <= step ? NX.primary : NX.border,
              transition: 'all 0.3s',
            }}
          />
        ))}
      </div>

      {step === 0 && (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: NX.fg,
              }}
            >
              Bienvenue, {userName} !
            </h2>
            <p style={{ fontSize: 14, color: NX.fgMuted, marginTop: 6 }}>
              Encore 2 petites étapes et c'est parti.
            </p>
          </div>

          {/* 09b5fe36 : ce cercle affichait un vrai sélecteur de fichier
              (label + input type="file" + FileReader) dont l'aperçu n'était
              jamais persisté nulle part — ni mutation, ni `avatarUrl` envoyé
              à `submitChoice()`. L'utilisateur voyait sa photo s'afficher et
              en déduisait, à raison, qu'elle était enregistrée. Retiré plutôt
              que rendu accessible (ce qui l'aurait juste rendu plus
              découvrable sans le rendre plus vrai) : même arbitrage que
              MAN-243. `Avatar` est le même composant que Réglages/le rail,
              purement décoratif ici — pas d'affordance qui promette un
              upload inexistant. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
            <Avatar name={userName} size={80} />
          </div>

          <Button onClick={() => setStep(1)} fullWidth size="lg">
            Continuer
          </Button>
        </div>
      )}

      {step === 1 && (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2
              style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: NX.fg,
              }}
            >
              Ton premier groupe
            </h2>
            <p style={{ fontSize: 14, color: NX.fgMuted, marginTop: 6 }}>
              Un groupe = ta bande. Tu peux en créer plusieurs.
            </p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <div
              role="radiogroup"
              aria-label="Créer ou rejoindre un groupe"
              style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <ChoiceCard
                ref={createOptionRef}
                icon="🚀"
                title="Créer un groupe"
                subtitle="Invite tes amis avec un lien"
                selected={choice === 'create'}
                tabIndex={(choice ?? 'create') === 'create' ? 0 : -1}
                onClick={() => selectChoice('create')}
                onKeyDown={handleOptionKeyDown('create')}
                tone="primary"
              />

              <ChoiceCard
                ref={joinOptionRef}
                icon="🔗"
                title="Rejoindre un groupe"
                subtitle="J'ai un lien d'invitation"
                selected={choice === 'join'}
                tabIndex={(choice ?? 'create') === 'join' ? 0 : -1}
                onClick={() => selectChoice('join')}
                onKeyDown={handleOptionKeyDown('join')}
                tone="info"
              />
            </div>

            {/* Champ associé sorti du role="radio" (MAN-119) : un `<Input>`
                imbriqué dans un `role="radio"` est du contenu interactif
                dans un contrôle qui n'en accepte pas — invalide et cassant
                pour les lecteurs d'écran. Affiché sous le groupe plutôt que
                dans la carte sélectionnée. */}
            {choice === 'create' && (
              <div style={{ marginTop: 14 }}>
                <Input
                  label="Nom du groupe"
                  name="groupName"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="La Bande du 11e"
                />
              </div>
            )}
            {choice === 'join' && (
              <div style={{ marginTop: 14 }}>
                <Input
                  label="Lien ou code d'invitation"
                  name="joinCode"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="nexusapp.chat/invite/..."
                />
              </div>
            )}
          </div>

          {error && (
            <div style={{ fontSize: 12, color: NX.error, textAlign: 'center', marginBottom: 8 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => setStep(0)}>
              ←
            </Button>
            <Button
              fullWidth
              size="lg"
              loading={busy}
              disabled={
                !choice ||
                (choice === 'create' && !groupName.trim()) ||
                (choice === 'join' && !joinCode.trim())
              }
              onClick={() => void submitChoice()}
            >
              {choice === 'create'
                ? 'Créer le groupe'
                : choice === 'join'
                  ? 'Rejoindre'
                  : 'Choisir une option'}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div
          className="animate-in fade-in slide-in-from-bottom-2 duration-300"
          style={{ textAlign: 'center' }}
        >
          <div
            className="animate-check-pop"
            style={{
              width: 64,
              height: 64,
              borderRadius: 20,
              background: 'rgba(52,211,153,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke={NX.success}
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: NX.fg,
            }}
          >
            C'est parti !
          </h2>
          <p
            style={{
              fontSize: 14,
              color: NX.fgMuted,
              marginTop: 8,
              lineHeight: 1.6,
              maxWidth: 320,
              margin: '8px auto 0',
            }}
          >
            {choice === 'create'
              ? `Ton groupe "${groupName}" est prêt. Partage le lien d'invitation à ta bande.`
              : 'Tu as rejoint le groupe. Tes conversations arrivent.'}
          </p>

          <div style={{ marginTop: 28 }}>
            <Button onClick={() => void navigate({ to: '/app' })} fullWidth size="lg">
              Ouvrir nexus
            </Button>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

interface ChoiceCardProps {
  icon: string;
  title: string;
  subtitle: string;
  selected: boolean;
  tabIndex: number;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  tone: 'primary' | 'info';
}

/**
 * Option d'un radiogroup à 2 choix (MAN-119). `role="radio"` ne doit porter
 * que du contenu non-interactif (name-from-content) — le champ conditionnel
 * associé (Input) est rendu par l'appelant, hors de cet élément.
 */
const ChoiceCard = forwardRef<HTMLDivElement, ChoiceCardProps>(function ChoiceCard(
  { icon, title, subtitle, selected, tabIndex, onClick, onKeyDown, tone },
  ref,
) {
  const [focusVisible, setFocusVisible] = useState(false);
  const accent = tone === 'primary' ? NX.primaryMuted : 'rgba(96,165,250,0.1)';
  return (
    <div
      ref={ref}
      role="radio"
      aria-checked={selected}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onFocus={(e) => setFocusVisible(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocusVisible(false)}
      style={{
        padding: 18,
        borderRadius: NX.radius,
        cursor: 'pointer',
        transition: 'all 0.2s',
        background: selected ? NX.primaryMuted : NX.elevated,
        border: `1px solid ${selected ? `${NX.primary}44` : NX.border}`,
        // Style inline (pas de classe Tailwind) : cf. WindowButton/MAN-121,
        // même contrainte, même pattern.
        boxShadow: focusVisible ? NX.shadowFocus : 'none',
        outline: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
          }}
        >
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: NX.fg }}>{title}</div>
          <div style={{ fontSize: 12, color: NX.fgDim, marginTop: 2 }}>{subtitle}</div>
        </div>
      </div>
    </div>
  );
});
